import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { List as ImmutableList } from 'immutable';
import lensProp from 'ramda/es/lensProp';
import not from 'ramda/es/not';
import over from 'ramda/es/over';
import propOr from 'ramda/es/propOr';
import map from 'ramda/es/map';
import { isMobile, isTabletPortrait } from '../../theme/media';

import {
  Nav,
  MainLink,
  Subnavigation,
  CategoryColumn,
  SecondaryLink,
  ToggleButton,
  TogglableItem,
  List,
  MainList,
  SubnavList,
  ActiveSubNav,
  ActiveSubNavLink,
  ActiveSubNavLinkWrapper,
  FormWrapper,
  SubnavLinks,
  MainLinkWrapper,
  SecondaryLinkWrapper,
} from './categoryNavigation.styles';
import { extract, getField } from '../../utils/cmsData';
import messages from './categoryNavigation.messages';
import { PlatformDetector } from '../platformDetector/platformDetector.component';
import { PlatformConditional } from '../rendering/platformConditional';
import { TogglableCategory } from './togglableCategory';
import { ContentManager } from '../../components/contentManager';
import { ClipItemsToWrapper } from './clipItemsToWrapper';
import { ariaLiveHandler } from '../../utils/accessibility';

const MAX_DESKTOP_LINKS_COUNT = 5;

export class CategoryNavigation extends PureComponent {
  static propTypes = {
    categories: PropTypes.object,
    activeMainCategory: PropTypes.string,
    activeCategory: PropTypes.string,
    form: PropTypes.object,
    hiddenByScroll: PropTypes.bool,
    location: PropTypes.object,
  };

  state = {
    isOpen: false,
    toggledCategory: null,
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.location !== this.props.location) {
      this.setState({ isOpen: false });
    }

    if (!prevState.isOpen && this.state.isOpen) {
      this.measure();
    }

    if (prevState.isOpen && !this.state.isOpen) {
      this.setState({ sizes: null });
    }

    if (!prevProps.hiddenByScroll && this.props.hiddenByScroll) {
      this.setState({ isOpen: false });
    }
  }

  get activeCategory() {
    return this.props.activeCategory;
  }

  get activeMainCategory() {
    return this.props.activeMainCategory;
  }

  get activeSubcategory() {
    const isSubActive = this.activeCategory !== this.activeMainCategory;
    return isSubActive ? this.activeCategory : null;
  }

  get activeSubcategories() {
    const activeCategory = this.props.categories.find(
      category => getField('name', category) === this.activeMainCategory,
    );
    return activeCategory
      ? getField('subcategories', activeCategory) || new ImmutableList()
      : new ImmutableList();
  }

  mainNavRef = React.createRef();
  subnavRef = React.createRef();
  subnavListRef = React.createRef();

  measure() {
    const items = this.mainNavRef.current.children;
    const subnav = this.subnavRef.current;

    if (!isMobile() && !isTabletPortrait()) {
      this.setState({
        sizes: map(el => el.getBoundingClientRect().width, items),
        leftOffset: items[0].getBoundingClientRect().left - subnav.getBoundingClientRect().left,
      });
    }
  }

  toggleOpen = () => {
    this.setState(over(lensProp('isOpen'), not));
  };

  renderMainCategoryLink = category => {
    const { name, categoryPageUrl = '/' } = extract({
      name: getField('name'),
      categoryPageUrl: getField('categoryPageUrl'),
    })(category);

    return (
      <MainLinkWrapper isOpen={this.state.isOpen} key={name}>
        <MainLink
          url={categoryPageUrl}
          isOpen={this.state.isOpen}
          isActive={this.props.activeCategory === name}
          isSubActive={this.activeSubcategory && this.activeMainCategory === name}
        >
          {name}
        </MainLink>
      </MainLinkWrapper>
    );
  };

  renderSecondaryCategoryLink = category => {
    const { name, categoryPageUrl = '/' } = extract({
      name: getField('name'),
      categoryPageUrl: getField('categoryPageUrl'),
    })(category);
    return (
      <SecondaryLinkWrapper key={name}>
        <SecondaryLink url={categoryPageUrl}>{name}</SecondaryLink>
      </SecondaryLinkWrapper>
    );
  };

  render() {
    return (
      <PlatformDetector>
        {({ isMobile, isTabletPortrait, isDesktop, isDesktopFull, isTabletLandscape }) => {
          const mainCategories = this.props.categories.slice(
            0,
            isMobile ? this.props.categories.size : MAX_DESKTOP_LINKS_COUNT,
          );

          return (
            <Fragment>
              <Nav hiddenByScroll={this.props.hiddenByScroll} role="navigation">
                <MainList inline ref={this.mainNavRef}>
                  <ClipItemsToWrapper wrapper={this.mainNavRef} items={mainCategories}>
                    {clippedCategories => clippedCategories.map(this.renderMainCategoryLink)}
                  </ClipItemsToWrapper>
                </MainList>

                <ToggleButton
                  onClick={this.toggleOpen}
                  isOpen={this.state.isOpen}
                  aria-expanded={this.state.isOpen}
                  aria-live={ariaLiveHandler()}
                >
                  <FormattedMessage {...messages[this.state.isOpen ? 'less' : 'more']} />
                </ToggleButton>
              </Nav>

              <Fragment>
                <PlatformConditional hideOnMobile hideOnTablet>
                  <Subnavigation isOpen={this.state.isOpen} isSubActive={!!this.activeSubcategory}>
                    <SubnavLinks
                      ref={this.subnavRef}
                      style={{ paddingLeft: this.state.leftOffset }}
                    >
                      {mainCategories.map((mainCategory, index) => (
                        <CategoryColumn
                          key={mainCategory.get('name') + String(index)}
                          style={{
                            width: propOr('auto', index, this.state.sizes),
                          }}
                        >
                          {(getField('subcategories', mainCategory) || new ImmutableList()).map(
                            this.renderSecondaryCategoryLink,
                          )}
                        </CategoryColumn>
                      ))}
                    </SubnavLinks>

                    {this.props.form && (
                      <FormWrapper>
                        <ContentManager
                          content={this.props.form}
                          componentProps={{
                            customClass: 'form--' + getField('type', this.props.form),
                          }}
                        />
                      </FormWrapper>
                    )}
                  </Subnavigation>
                </PlatformConditional>

                <PlatformConditional hideOnDesktop hideOnTabletLarge hideOnDesktopLarge>
                  <Subnavigation isOpen={this.state.isOpen}>
                    <SubnavLinks>
                      <List>
                        {this.props.categories.map((mainCategory, index) => (
                          <TogglableItem key={mainCategory.get('name') + String(index)}>
                            <TogglableCategory
                              category={mainCategory}
                              isOpen={this.state.toggledCategory === mainCategory}
                              onToggle={isOpen =>
                                this.setState({ toggledCategory: isOpen ? mainCategory : null })
                              }
                            />
                          </TogglableItem>
                        ))}
                      </List>
                    </SubnavLinks>
                  </Subnavigation>
                </PlatformConditional>
              </Fragment>
              {(((isTabletLandscape || isDesktop || isDesktopFull) && this.activeSubcategory) ||
                ((isTabletPortrait || isMobile) && this.activeSubcategory && !this.state.isOpen)) &&
                this.activeSubcategories.size > 0 && (
                <ActiveSubNav hiddenByScroll={this.props.hiddenByScroll}>
                  <SubnavList ref={this.subnavListRef}>
                    <ClipItemsToWrapper
                      wrapper={this.subnavListRef}
                      items={this.activeSubcategories}
                    >
                      {categories =>
                        categories.map((category, index) => (
                          <ActiveSubNavLinkWrapper key={index}>
                            <ActiveSubNavLink
                              url={getField('categoryPageUrl', category) || '/'}
                              isActive={this.activeCategory === getField('name', category)}
                              tabIndex={this.state.isOpen ? -1 : 0}
                            >
                              {getField('name', category)}
                            </ActiveSubNavLink>
                          </ActiveSubNavLinkWrapper>
                        ))
                      }
                    </ClipItemsToWrapper>
                  </SubnavList>
                </ActiveSubNav>
              )}
            </Fragment>
          );
        }}
      </PlatformDetector>
    );
  }
}
